import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/shared/empty-state";
import { StatusBadge } from "@/components/shared/status-badge";
import { ROUTES } from "@/app/routes";
import { getApiCapabilityBindings, getApiCapabilityTargets, postApiCapabilityBindings, postApiCapabilityTargets } from "@/lib/api/generated/capabilities/capabilities";
import { useGetApiConnectorInstalls } from "@/lib/api/generated/channels/channels";
import { postApiProvidersDescriptors } from "@/lib/api/generated/providers/providers";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { queryKeys } from "@/lib/query/keys";
import { cn } from "@/lib/utils/cn";
import type {
  ConnectorInstallRecord,
  JsonObject,
  JsonValue,
  ProviderDescriptorField,
  ProviderDescriptorRecord,
  TenantCapabilityBindingRecord,
  TenantCapabilityTargetRecord,
} from "../../../../../packages/shared-types/src/index.js";
import {
  createProviderTargetFieldValues,
  getInstallableProviderDescriptors,
  parseProviderTargetFieldValues,
} from "./provider-descriptor-helpers.js";

const EMPTY_JSON = "{}";

type BindingFormState = {
  id: string;
  capabilityKey: string;
  status: TenantCapabilityBindingRecord["status"];
  defaultTargetKey: string;
  credentialMode: TenantCapabilityBindingRecord["credentialMode"];
  requiredAssuranceLevel: TenantCapabilityBindingRecord["requiredAssuranceLevel"];
  policy: string;
  defaults: string;
  permissions: string;
};

type TargetFormState = {
  id: string;
  bindingId: string;
  status: TenantCapabilityTargetRecord["status"];
  fieldValues: Record<string, string>;
};

export function ConnectorImplementationsPage() {
  const { tenantId: activeTenantId } = useTenantContext();
  const queryClient = useQueryClient();
  const [selectedConnectorInstallId, setSelectedConnectorInstallId] = useState("");
  const [bindingForm, setBindingForm] = useState<BindingFormState>({
    id: "",
    capabilityKey: "",
    status: "active",
    defaultTargetKey: "",
    credentialMode: "service_account",
    requiredAssuranceLevel: "level_0",
    policy: EMPTY_JSON,
    defaults: EMPTY_JSON,
    permissions: EMPTY_JSON,
  });
  const [targetForm, setTargetForm] = useState<TargetFormState>({
    id: "",
    bindingId: "",
    status: "active",
    fieldValues: {},
  });

  const { data: providerDescriptors = [] } = useQuery({
    queryKey: ["providers-descriptors", activeTenantId],
    queryFn: () => postApiProvidersDescriptors({ tenantId: activeTenantId }),
    enabled: Boolean(activeTenantId),
  });
  const { data: bindings = [] } = useQuery({
    queryKey: queryKeys.tenantCapabilityBindings(activeTenantId),
    queryFn: () => getApiCapabilityBindings(),
    enabled: Boolean(activeTenantId),
  });
  const { data: targets = [] } = useQuery({
    queryKey: queryKeys.tenantCapabilityTargets(activeTenantId),
    queryFn: () => getApiCapabilityTargets(),
    enabled: Boolean(activeTenantId),
  });
  const { data: connectorInstalls = [] } = useGetApiConnectorInstalls<ConnectorInstallRecord[]>({
    query: { enabled: Boolean(activeTenantId) },
  });

  const saveBinding = useMutation({
    mutationFn: (record: TenantCapabilityBindingRecord) =>
      postApiCapabilityBindings({ record: record as unknown as Record<string, unknown> }),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCapabilityBindings(input.tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.state });
    },
  });
  const saveTarget = useMutation({
    mutationFn: (record: TenantCapabilityTargetRecord) =>
      postApiCapabilityTargets({ record: record as unknown as Record<string, unknown> }),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCapabilityTargets(input.tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.state });
    },
  });

  const targetDraft = useMemo(
    () =>
      targetForm.bindingId
        ? {
            id: targetForm.id,
            bindingId: targetForm.bindingId,
            fieldValues: targetForm.fieldValues,
          }
        : undefined,
    [targetForm],
  );

  const implementationsModel = useMemo(
    () =>
      buildConnectorWorkspaceData({
        connectorInstalls,
        providerDescriptors: providerDescriptors as unknown as ProviderDescriptorRecord[],
        bindings: bindings as unknown as TenantCapabilityBindingRecord[],
        targets: targets as unknown as TenantCapabilityTargetRecord[],
        selectedConnectorInstallId,
        targetDraft,
      }),
    [connectorInstalls, providerDescriptors, bindings, targets, selectedConnectorInstallId, targetDraft],
  );

  const selectedWorkspace = implementationsModel.selectedWorkspace;
  const selectedDescriptor = selectedWorkspace?.descriptor;
  const capabilityOptions = useMemo(
    () => getConnectorCapabilityOptions(selectedDescriptor),
    [selectedDescriptor],
  );
  const selectedBindings = selectedWorkspace?.bindings ?? [];
  const selectedTargets = selectedWorkspace?.targets ?? [];
  const targetFields = useMemo(
    () => getConnectorTargetFields(selectedDescriptor),
    [selectedDescriptor],
  );
  const isGraphRag = selectedWorkspace?.install.connectorKey === "graphrag";

  useDocumentTitle("Connector Setup");

  useEffect(() => {
    if (!selectedConnectorInstallId && implementationsModel.selectedWorkspace?.install.id) {
      setSelectedConnectorInstallId(implementationsModel.selectedWorkspace.install.id);
    }
  }, [selectedConnectorInstallId, implementationsModel.selectedWorkspace]);

  useEffect(() => {
    if (!selectedWorkspace) {
      return;
    }

    if (
      selectedBindings.some((binding) => binding.id === bindingForm.id) &&
      (targetForm.bindingId === "" || selectedBindings.some((binding) => binding.id === targetForm.bindingId))
    ) {
      return;
    }

    const firstBinding = selectedBindings[0];
    if (firstBinding) {
      loadBindingIntoForm(firstBinding, setBindingForm);
      const firstTarget = selectedTargets.find((target) => target.bindingId === firstBinding.id);
      if (firstTarget) {
        loadTargetIntoForm(firstTarget, selectedDescriptor, setTargetForm);
      } else {
        setTargetForm(createEmptyTargetForm(selectedWorkspace.install, selectedDescriptor, firstBinding.id));
      }
      return;
    }

    setBindingForm(createEmptyBindingForm(selectedWorkspace.install, selectedDescriptor));
    setTargetForm(createEmptyTargetForm(selectedWorkspace.install, selectedDescriptor, ""));
  }, [selectedWorkspace, selectedBindings, selectedTargets, selectedDescriptor, bindingForm.id, targetForm.bindingId]);

  async function handleSaveBinding() {
    if (!selectedWorkspace || !selectedDescriptor) {
      toast.error("Select an installed backend first.");
      return;
    }
    try {
      const record: TenantCapabilityBindingRecord = {
        id: bindingForm.id.trim(),
        tenantId: activeTenantId,
        capabilityKey: bindingForm.capabilityKey.trim(),
        descriptorKey: selectedDescriptor.descriptorKey,
        executionHost: "runtime_connector",
        connectorInstallId: selectedWorkspace.install.id,
        providerKey: selectedDescriptor.providerKey,
        credentialMode: bindingForm.credentialMode ?? "service_account",
        requiredAssuranceLevel: bindingForm.requiredAssuranceLevel ?? "level_0",
        status: bindingForm.status,
        defaultTargetKey: bindingForm.defaultTargetKey.trim() || undefined,
        policy: parseJson(bindingForm.policy, "Binding policy"),
        defaults: parseJson(bindingForm.defaults, "Binding defaults"),
        permissions: parseJson(bindingForm.permissions, "Binding permissions"),
        updatedAt: new Date().toISOString(),
      };
      await saveBinding.mutateAsync(record);
      setTargetForm((current) =>
        current.bindingId
          ? current
          : createEmptyTargetForm(selectedWorkspace.install, selectedDescriptor, record.id),
      );
      toast.success(`Saved binding ${record.id}`);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save binding"));
    }
  }

  async function handleSaveTarget() {
    if (!selectedDescriptor) {
      toast.error("Select an installed backend first.");
      return;
    }
    try {
      const parsed = parseProviderTargetFieldValues(selectedDescriptor, targetForm.fieldValues);
      if (!parsed.ok) {
        throw new Error(parsed.message);
      }
      const record: TenantCapabilityTargetRecord = {
        id: targetForm.id.trim(),
        tenantId: activeTenantId,
        bindingId: targetForm.bindingId.trim(),
        targetKey: parsed.targetKey,
        displayName: parsed.displayName,
        providerTargetRef: parsed.providerTargetRef,
        status: targetForm.status,
        metadata: parsed.metadata,
      };
      await saveTarget.mutateAsync(record);
      setBindingForm((current) =>
        current.defaultTargetKey ? current : { ...current, defaultTargetKey: record.targetKey },
      );
      toast.success(`Saved ${isGraphRag ? "source" : "target"} ${record.displayName}`);
    } catch (error) {
      toast.error(getErrorMessage(error, `Failed to save ${isGraphRag ? "source" : "target"}`));
    }
  }

  const header = (
    <Card>
      <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-1.5">
          <span
            className="text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--muted-foreground)" }}
          >
            Connector setup
          </span>
          <CardTitle>Configure installed backends</CardTitle>
          <p className="max-w-3xl text-sm" style={{ color: "var(--muted-foreground)" }}>
            Start from an installed backend, map it to the capability it powers, configure concrete destinations, and preview the exact tools that will reach agents.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm">
            <Link to={ROUTES.tenantConnectorInstallNew(activeTenantId)}>Install backend</Link>
          </Button>
        </div>
      </CardHeader>
    </Card>
  );

  if (implementationsModel.workspaces.length === 0) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <EmptyState
          title="No installed backends yet"
          description="Install a connector backend first, then return here to bind capabilities, configure sources, and preview agent tools."
          action={{
            label: "Install backend",
            onClick: () => {
              window.location.href = ROUTES.tenantConnectorInstallNew(activeTenantId);
            },
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {header}

      <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <Card>
          <CardHeader className="gap-2">
            <CardTitle>Installed backends</CardTitle>
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              Choose the backend you want to wire. This page only deals with runtime connectors, not first-party control-plane apps.
            </p>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {implementationsModel.workspaces.map((workspace) => {
              const descriptorLabel = workspace.descriptor?.displayName ?? workspace.install.connectorKey;
              const isSelected = workspace.install.id === selectedWorkspace?.install.id;
              return (
                <button
                  key={workspace.install.id}
                  type="button"
                  onClick={() => setSelectedConnectorInstallId(workspace.install.id)}
                  className={cn(
                    "rounded-md border px-3 py-3 text-left transition-colors hover:border-[var(--border-hover)]",
                    isSelected ? "border-[var(--accent)] bg-[var(--bg-accent)]" : "",
                  )}
                  style={{ borderColor: isSelected ? "var(--accent)" : "var(--border)" }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium" style={{ color: "var(--text-strong)" }}>
                        {descriptorLabel}
                      </div>
                      <div className="text-xs font-mono" style={{ color: "var(--muted-foreground)" }}>
                        {workspace.install.id}
                      </div>
                    </div>
                    <StatusBadge status={workspace.install.status} />
                  </div>
                  <div className="mt-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                    {workspace.setupDescription}
                  </div>
                  <div className="mt-2 text-[11px]" style={{ color: "var(--muted-foreground)" }}>
                    {workspace.bindings.length} binding{workspace.bindings.length === 1 ? "" : "s"} · {workspace.targets.length} {workspace.install.connectorKey === "graphrag" ? "source" : "target"}{workspace.targets.length === 1 ? "" : "s"}
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {selectedWorkspace ? (
          <div className="flex flex-col gap-6">
            <Card>
              <CardHeader className="gap-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle>{selectedWorkspace.descriptor?.displayName ?? selectedWorkspace.install.connectorKey}</CardTitle>
                    <p className="mt-1 text-xs" style={{ color: "var(--muted-foreground)" }}>
                      {selectedWorkspace.descriptor?.description ?? "Installed runtime backend."}
                    </p>
                  </div>
                  <StatusBadge status={selectedWorkspace.install.status} />
                </div>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-3">
                <MetricCard
                  label="Setup"
                  value={selectedWorkspace.setupCallToAction}
                  description={selectedWorkspace.setupDescription}
                />
                <MetricCard
                  label="Bindings"
                  value={String(selectedWorkspace.bindings.length)}
                  description="Capabilities currently powered by this backend."
                />
                <MetricCard
                  label={isGraphRag ? "Agent tools" : "Targets"}
                  value={String(selectedWorkspace.toolPreviews.length)}
                  description={
                    isGraphRag
                      ? "Target-scoped tools that will appear to agents."
                      : "Preview entries derived from connector bindings."
                  }
                />
              </CardContent>
            </Card>

            <div className="grid gap-6 xl:grid-cols-2">
              <SectionCard
                title="Binding"
                description="Choose which capability this installed backend powers."
              >
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Binding ID">
                    <input
                      className="form-input"
                      value={bindingForm.id}
                      onChange={(event) =>
                        setBindingForm((current) => ({ ...current, id: event.target.value }))
                      }
                    />
                  </Field>
                  <Field
                    label="Installed backend"
                    hint="This binding is scoped to the selected connector instance."
                  >
                    <div className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--border)" }}>
                      {selectedWorkspace.install.id} · {selectedWorkspace.install.connectorKey}
                    </div>
                  </Field>
                  <Field
                    label="Capability"
                    hint="Select the runtime capability this connector instance should satisfy."
                  >
                    <select
                      className="form-input"
                      value={bindingForm.capabilityKey}
                      onChange={(event) =>
                        setBindingForm((current) => ({
                          ...current,
                          capabilityKey: event.target.value,
                        }))
                      }
                    >
                      <option value="">Select capability</option>
                      {capabilityOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label} · {option.value}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Status">
                    <select
                      className="form-input"
                      value={bindingForm.status}
                      onChange={(event) =>
                        setBindingForm((current) => ({
                          ...current,
                          status: event.target.value as TenantCapabilityBindingRecord["status"],
                        }))
                      }
                    >
                      <option value="active">active</option>
                      <option value="paused">paused</option>
                      <option value="error">error</option>
                    </select>
                  </Field>
                  <Field
                    label={isGraphRag ? "Default Source Key" : "Default Target Key"}
                    hint="Used automatically when the runtime does not receive a target override."
                  >
                    <input
                      className="form-input"
                      value={bindingForm.defaultTargetKey}
                      onChange={(event) =>
                        setBindingForm((current) => ({
                          ...current,
                          defaultTargetKey: event.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field label="Credential mode">
                    <select
                      className="form-input"
                      value={bindingForm.credentialMode}
                      onChange={(event) =>
                        setBindingForm((current) => ({
                          ...current,
                          credentialMode: event.target.value as TenantCapabilityBindingRecord["credentialMode"],
                        }))
                      }
                    >
                      <option value="service_account">service account</option>
                      <option value="delegated_user">delegated user</option>
                      <option value="admin_impersonation">admin impersonation</option>
                    </select>
                  </Field>
                  <Field label="Required assurance">
                    <select
                      className="form-input"
                      value={bindingForm.requiredAssuranceLevel}
                      onChange={(event) =>
                        setBindingForm((current) => ({
                          ...current,
                          requiredAssuranceLevel:
                            event.target.value as TenantCapabilityBindingRecord["requiredAssuranceLevel"],
                        }))
                      }
                    >
                      <option value="level_0">level 0</option>
                      <option value="level_1">level 1</option>
                      <option value="level_2">level 2</option>
                    </select>
                  </Field>
                </div>

                <details>
                  <summary
                    className="cursor-pointer text-xs font-medium"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Advanced binding JSON
                  </summary>
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    <JsonField
                      label="Policy"
                      value={bindingForm.policy}
                      onChange={(value) => setBindingForm((current) => ({ ...current, policy: value }))}
                    />
                    <JsonField
                      label="Defaults"
                      value={bindingForm.defaults}
                      onChange={(value) => setBindingForm((current) => ({ ...current, defaults: value }))}
                    />
                    <JsonField
                      label="Permissions"
                      value={bindingForm.permissions}
                      onChange={(value) => setBindingForm((current) => ({ ...current, permissions: value }))}
                    />
                  </div>
                </details>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveBinding}
                    disabled={saveBinding.isPending || !bindingForm.id.trim() || !bindingForm.capabilityKey.trim()}
                  >
                    {saveBinding.isPending ? "Saving..." : "Save binding"}
                  </Button>
                </div>

                <RecordList
                  records={selectedBindings}
                  selectedKey={bindingForm.id}
                  onSelect={(record) => {
                    loadBindingIntoForm(record, setBindingForm);
                    const firstTarget = selectedTargets.find((target) => target.bindingId === record.id);
                    if (firstTarget) {
                      loadTargetIntoForm(firstTarget, selectedDescriptor, setTargetForm);
                    } else {
                      setTargetForm(createEmptyTargetForm(selectedWorkspace.install, selectedDescriptor, record.id));
                    }
                  }}
                  renderKey={(record) => record.id}
                  renderBody={(record) => (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium" style={{ color: "var(--text-strong)" }}>
                            {record.capabilityKey}
                          </div>
                          <div className="text-xs font-mono" style={{ color: "var(--muted-foreground)" }}>
                            {record.id}
                          </div>
                        </div>
                        <StatusBadge status={record.status} />
                      </div>
                      <div className="mt-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                        Default {isGraphRag ? "source" : "target"}: {record.defaultTargetKey ?? "none"}
                      </div>
                    </>
                  )}
                  emptyTitle="No bindings yet"
                  emptyDescription={`Create a binding so ${selectedWorkspace.install.connectorKey} can power a runtime capability.`}
                />
              </SectionCard>

              <SectionCard
                title={isGraphRag ? "Source" : "Target"}
                description={
                  isGraphRag
                    ? "Configure concrete GraphRAG workspaces and the exact tool metadata agents will see."
                    : "Configure concrete destinations for this connector."
                }
              >
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label={isGraphRag ? "Source ID" : "Target ID"}>
                    <input
                      className="form-input"
                      value={targetForm.id}
                      onChange={(event) =>
                        setTargetForm((current) => ({ ...current, id: event.target.value }))
                      }
                    />
                  </Field>
                  <Field
                    label="Binding"
                    hint="Attach this destination to one binding for the selected connector."
                  >
                    <select
                      className="form-input"
                      value={targetForm.bindingId}
                      onChange={(event) =>
                        setTargetForm((current) => ({ ...current, bindingId: event.target.value }))
                      }
                    >
                      <option value="">Select binding</option>
                      {selectedBindings.map((binding) => (
                        <option key={binding.id} value={binding.id}>
                          {binding.capabilityKey} · {binding.id}
                        </option>
                      ))}
                    </select>
                  </Field>

                  {targetFields.map((field) => {
                    const presentation = getConnectorTargetFieldPresentation({
                      descriptor: selectedDescriptor,
                      field,
                    });
                    return (
                      <Field key={field.key} label={presentation.label} hint={presentation.description}>
                        {field.type === "json" ? (
                          <textarea
                            className="form-input min-h-28 font-mono text-xs"
                            value={targetForm.fieldValues[field.key] ?? ""}
                            placeholder={
                              presentation.placeholder ??
                              (field.defaultValue !== undefined
                                ? JSON.stringify(field.defaultValue, null, 2)
                                : "{}")
                            }
                            onChange={(event) =>
                              setTargetForm((current) => ({
                                ...current,
                                fieldValues: {
                                  ...current.fieldValues,
                                  [field.key]: event.target.value,
                                },
                              }))
                            }
                          />
                        ) : (
                          <input
                            className="form-input"
                            value={targetForm.fieldValues[field.key] ?? ""}
                            placeholder={presentation.placeholder}
                            onChange={(event) =>
                              setTargetForm((current) => ({
                                ...current,
                                fieldValues: {
                                  ...current.fieldValues,
                                  [field.key]: event.target.value,
                                },
                              }))
                            }
                          />
                        )}
                      </Field>
                    );
                  })}

                  <Field label="Status">
                    <select
                      className="form-input"
                      value={targetForm.status}
                      onChange={(event) =>
                        setTargetForm((current) => ({
                          ...current,
                          status: event.target.value as TenantCapabilityTargetRecord["status"],
                        }))
                      }
                    >
                      <option value="active">active</option>
                      <option value="paused">paused</option>
                    </select>
                  </Field>
                </div>

                {!targetFields.length ? (
                  <EmptyState
                    title="This backend does not declare target fields"
                    description="The selected connector descriptor does not expose named target metadata."
                  />
                ) : null}

                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveTarget}
                    disabled={
                      saveTarget.isPending ||
                      !targetForm.id.trim() ||
                      !targetForm.bindingId.trim() ||
                      targetFields.length === 0
                    }
                  >
                    {saveTarget.isPending ? "Saving..." : `Save ${isGraphRag ? "source" : "target"}`}
                  </Button>
                </div>

                <RecordList
                  records={selectedTargets}
                  selectedKey={targetForm.id}
                  onSelect={(record) => {
                    const binding = selectedBindings.find((entry) => entry.id === record.bindingId);
                    if (binding) {
                      loadBindingIntoForm(binding, setBindingForm);
                    }
                    loadTargetIntoForm(record, selectedDescriptor, setTargetForm);
                  }}
                  renderKey={(record) => record.id}
                  renderBody={(record) => (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium" style={{ color: "var(--text-strong)" }}>
                            {record.displayName}
                          </div>
                          <div className="text-xs font-mono" style={{ color: "var(--muted-foreground)" }}>
                            {record.id}
                          </div>
                        </div>
                        <StatusBadge status={record.status} />
                      </div>
                      <div className="mt-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                        {record.targetKey} · {record.providerTargetRef}
                      </div>
                      <div className="mt-1 text-[11px]" style={{ color: "var(--muted-foreground)" }}>
                        Binding {record.bindingId}
                      </div>
                    </>
                  )}
                  emptyTitle={`No ${isGraphRag ? "sources" : "targets"} yet`}
                  emptyDescription={
                    isGraphRag
                      ? "Add at least one GraphRAG source so the agent gets a concrete workspace-scoped tool."
                      : "Add at least one target so this connector can route work to the right destination."
                  }
                />
              </SectionCard>
            </div>

            <SectionCard
              title="Runtime tool preview"
              description={
                isGraphRag
                  ? "Exactly how this connector will surface agent-facing tools once policy allows them."
                  : "Derived preview of the connector-backed tools exposed by these bindings."
              }
            >
              <RecordList
                records={selectedWorkspace.toolPreviews}
                renderKey={(record) => record.id}
                renderBody={(record) => (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium" style={{ color: "var(--text-strong)" }}>
                          {record.title}
                        </div>
                        <div className="mt-1 text-xs" style={{ color: "var(--muted-foreground)" }}>
                          {record.description}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {record.isDefault ? <BadgeLike>Default</BadgeLike> : null}
                        {record.status === "draft" ? <BadgeLike>Draft preview</BadgeLike> : null}
                      </div>
                    </div>
                    <div className="mt-2 text-[11px]" style={{ color: "var(--muted-foreground)" }}>
                      {record.supportingText}
                    </div>
                  </>
                )}
                emptyTitle="No runtime tools to preview yet"
                emptyDescription={
                  isGraphRag
                    ? "Create a binding and add at least one source to generate agent-facing GraphRAG tools."
                    : "Create a binding to preview the connector-backed runtime tool surface."
                }
              />
            </SectionCard>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="gap-2">
        <CardTitle>{title}</CardTitle>
        <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
          {description}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">{children}</CardContent>
    </Card>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {hint ? (
        <div className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function JsonField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label}>
      <textarea
        className="form-input min-h-28 font-mono text-xs"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        spellCheck={false}
      />
    </Field>
  );
}

function RecordList<T>({
  records,
  renderKey,
  renderBody,
  emptyTitle,
  emptyDescription,
  selectedKey,
  onSelect,
}: {
  records: readonly T[];
  renderKey: (record: T) => string;
  renderBody: (record: T) => React.ReactNode;
  emptyTitle: string;
  emptyDescription: string;
  selectedKey?: string;
  onSelect?: (record: T) => void;
}) {
  if (records.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className="grid gap-3">
      {records.map((record) => {
        const key = renderKey(record);
        const isSelected = selectedKey === key;
        const className = cn(
          "rounded-md border px-3 py-3 text-left transition-colors",
          onSelect ? "cursor-pointer hover:border-[var(--border-hover)] hover:bg-[var(--bg-hover)]" : "",
          isSelected ? "border-[var(--accent)] bg-[var(--bg-accent)]" : "",
        );

        if (onSelect) {
          return (
            <button
              key={key}
              type="button"
              className={className}
              style={{ borderColor: isSelected ? "var(--accent)" : "var(--border)" }}
              onClick={() => onSelect(record)}
            >
              {renderBody(record)}
            </button>
          );
        }

        return (
          <div key={key} className={className} style={{ borderColor: "var(--border)" }}>
            {renderBody(record)}
          </div>
        );
      })}
    </div>
  );
}

function MetricCard({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description: string;
}) {
  return (
    <div className="rounded-md border px-4 py-4" style={{ borderColor: "var(--border)", background: "var(--panel)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--muted-foreground)" }}>
        {label}
      </div>
      <div className="mt-2 text-lg font-semibold" style={{ color: "var(--text-strong)" }}>
        {value}
      </div>
      <div className="mt-1 text-xs" style={{ color: "var(--muted-foreground)" }}>
        {description}
      </div>
    </div>
  );
}

function BadgeLike({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em]"
      style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
    >
      {children}
    </span>
  );
}

function createEmptyBindingForm(
  install: ConnectorInstallRecord,
  descriptor: ProviderDescriptorRecord | undefined,
): BindingFormState {
  const firstCapability = getConnectorCapabilityOptions(descriptor)[0]?.value ?? "";
  return {
    id: buildEmptyBindingId(install, descriptor),
    capabilityKey: firstCapability,
    status: "active",
    defaultTargetKey: "",
    credentialMode: "service_account",
    requiredAssuranceLevel: "level_0",
    policy: EMPTY_JSON,
    defaults: EMPTY_JSON,
    permissions: EMPTY_JSON,
  };
}

function createEmptyTargetForm(
  install: ConnectorInstallRecord,
  descriptor: ProviderDescriptorRecord | undefined,
  bindingId: string,
): TargetFormState {
  return {
    id: buildEmptyTargetId(install),
    bindingId,
    status: "active",
    fieldValues: createProviderTargetFieldValues(descriptor),
  };
}

function loadBindingIntoForm(
  record: TenantCapabilityBindingRecord,
  setBindingForm: React.Dispatch<React.SetStateAction<BindingFormState>>,
) {
  setBindingForm({
    id: record.id,
    capabilityKey: record.capabilityKey,
    status: record.status,
    defaultTargetKey: record.defaultTargetKey ?? "",
    credentialMode: record.credentialMode ?? "service_account",
    requiredAssuranceLevel: record.requiredAssuranceLevel ?? "level_0",
    policy: stringifyJson(record.policy),
    defaults: stringifyJson(record.defaults),
    permissions: stringifyJson(record.permissions),
  });
}

function loadTargetIntoForm(
  record: TenantCapabilityTargetRecord,
  descriptor: ProviderDescriptorRecord | undefined,
  setTargetForm: React.Dispatch<React.SetStateAction<TargetFormState>>,
) {
  setTargetForm({
    id: record.id,
    bindingId: record.bindingId,
    status: record.status,
    fieldValues: createProviderTargetFieldValues(descriptor, {
      targetKey: record.targetKey,
      displayName: record.displayName,
      providerTargetRef: record.providerTargetRef,
      metadata: record.metadata,
    }),
  });
}

function parseJson(value: string, label: string): JsonObject {
  try {
    return toJsonObject(JSON.parse(value || "{}"), label);
  } catch {
    throw new Error(`${label} must be valid JSON.`);
  }
}

function toJsonObject(value: unknown, label: string): JsonObject {
  if (!isJsonObject(value)) {
    throw new Error(`${label} must be a JSON object.`);
  }
  return value;
}

function isJsonObject(value: unknown): value is JsonObject {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  return Object.values(value).every(isJsonValue);
}

function isJsonValue(value: unknown): value is JsonValue {
  if (value === null) {
    return true;
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.every(isJsonValue);
  }
  return isJsonObject(value);
}

function stringifyJson(value: JsonObject | undefined): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

type ConnectorToolPreview = {
  id: string;
  title: string;
  description: string;
  supportingText: string;
  isDefault: boolean;
  status: "saved" | "draft";
};

type ConnectorWorkspaceData = {
  install: ConnectorInstallRecord;
  descriptor?: ProviderDescriptorRecord;
  bindings: TenantCapabilityBindingRecord[];
  targets: TenantCapabilityTargetRecord[];
  toolPreviews: ConnectorToolPreview[];
  setupCallToAction: string;
  setupDescription: string;
};

export function buildConnectorWorkspaceData(params: {
  connectorInstalls: ConnectorInstallRecord[] | undefined;
  providerDescriptors: ProviderDescriptorRecord[] | undefined;
  bindings: TenantCapabilityBindingRecord[] | undefined;
  targets: TenantCapabilityTargetRecord[] | undefined;
  selectedConnectorInstallId?: string;
  targetDraft?: { id: string; bindingId: string; fieldValues: Record<string, string> };
}): {
  workspaces: ConnectorWorkspaceData[];
  selectedWorkspace?: ConnectorWorkspaceData;
} {
  const descriptors = getInstallableProviderDescriptors(params.providerDescriptors);
  const descriptorByConnectorKey = new Map(
    descriptors.map((descriptor) => [descriptor.connectorKey ?? descriptor.providerKey, descriptor] as const),
  );
  const runtimeBindings = (params.bindings ?? [])
    .filter((binding) => binding.executionHost === "runtime_connector" && binding.connectorInstallId)
    .sort((left, right) => left.capabilityKey.localeCompare(right.capabilityKey));
  const runtimeTargets = (params.targets ?? []).sort((left, right) =>
    `${left.displayName} ${left.targetKey}`.localeCompare(`${right.displayName} ${right.targetKey}`),
  );
  const targetsByBindingId = new Map<string, TenantCapabilityTargetRecord[]>();
  for (const target of runtimeTargets) {
    const existing = targetsByBindingId.get(target.bindingId) ?? [];
    existing.push(target);
    targetsByBindingId.set(target.bindingId, existing);
  }

  const workspaces = (params.connectorInstalls ?? [])
    .map((install) => {
      const descriptor = descriptorByConnectorKey.get(install.connectorKey);
      const workspaceBindings = runtimeBindings.filter((binding) => binding.connectorInstallId === install.id);
      const workspaceTargets = workspaceBindings.flatMap((binding) => targetsByBindingId.get(binding.id) ?? []);
      return {
        install,
        descriptor,
        bindings: workspaceBindings,
        targets: workspaceTargets,
        toolPreviews: buildConnectorToolPreviews({
          install,
          descriptor,
          bindings: workspaceBindings,
          targetsByBindingId,
          targetDraft:
            params.targetDraft && workspaceBindings.some((binding) => binding.id === params.targetDraft?.bindingId)
              ? params.targetDraft
              : undefined,
        }),
        ...summarizeConnectorSetup(descriptor, workspaceBindings, workspaceTargets),
      };
    })
    .sort((left, right) => {
      const leftLabel = left.descriptor?.displayName ?? left.install.connectorKey;
      const rightLabel = right.descriptor?.displayName ?? right.install.connectorKey;
      return leftLabel.localeCompare(rightLabel);
    });

  return {
    workspaces,
    selectedWorkspace:
      workspaces.find((workspace) => workspace.install.id === params.selectedConnectorInstallId) ??
      workspaces[0],
  };
}

export function getConnectorCapabilityOptions(
  descriptor: ProviderDescriptorRecord | undefined,
): Array<{ value: string; label: string; description?: string }> {
  return (descriptor?.capabilities ?? [])
    .map((capability) => ({
      value: capability.capabilityKey,
      label: capability.name?.trim() || capability.capabilityKey,
      description: capability.description?.trim() || undefined,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function getConnectorTargetFields(
  descriptor: ProviderDescriptorRecord | undefined,
): ProviderDescriptorField[] {
  if (!descriptor?.targets?.fields?.length) {
    return [];
  }
  const isGraphRag = descriptor.connectorKey === "graphrag";
  return descriptor.targets.fields.filter((field) => !(isGraphRag && field.key === "metadata"));
}

export function getConnectorTargetFieldPresentation(params: {
  descriptor: ProviderDescriptorRecord | undefined;
  field: ProviderDescriptorField;
}): { label: string; description?: string; placeholder?: string } {
  const { descriptor, field } = params;
  if (descriptor?.connectorKey !== "graphrag") {
    return { label: field.label, description: field.description, placeholder: field.placeholder };
  }
  switch (field.key) {
    case "targetKey":
      return { label: "Source Key", description: "Stable key for this GraphRAG source inside the tenant.", placeholder: "support-kb" };
    case "displayName":
      return { label: "Source Name", description: "Human-friendly label for this GraphRAG source.", placeholder: "Support Knowledge Base" };
    case "providerTargetRef":
      return { label: "Workspace ID", description: "GraphRAG workspace ID used by this source.", placeholder: "workspace_support" };
    case "toolAlias":
      return { label: "Agent Tool Name", description: "Exact tool name the agent will see for this source.", placeholder: "support_kb_search" };
    case "toolDescription":
      return { label: "Agent Tool Description", description: "Agent-facing description explaining when to use this source.", placeholder: "Use this source for support policy, troubleshooting, and ticket questions." };
    default:
      return { label: field.label, description: field.description, placeholder: field.placeholder };
  }
}

function buildEmptyBindingId(
  install: ConnectorInstallRecord,
  descriptor: ProviderDescriptorRecord | undefined,
): string {
  const firstCapabilityKey = descriptor?.capabilities?.[0]?.capabilityKey ?? install.connectorKey;
  const suffix = firstCapabilityKey.replace(/[^a-z0-9]+/gi, "_").toLowerCase();
  return `binding_${install.connectorKey.replace(/[^a-z0-9]+/gi, "_").toLowerCase()}_${suffix}`;
}

function buildEmptyTargetId(install: ConnectorInstallRecord): string {
  return `target_${install.connectorKey.replace(/[^a-z0-9]+/gi, "_").toLowerCase()}_${Date.now()}`;
}

function summarizeConnectorSetup(
  descriptor: ProviderDescriptorRecord | undefined,
  bindings: TenantCapabilityBindingRecord[],
  targets: TenantCapabilityTargetRecord[],
): Pick<ConnectorWorkspaceData, "setupCallToAction" | "setupDescription"> {
  if (bindings.length === 0) {
    return {
      setupCallToAction: "Create binding",
      setupDescription: "This connector is installed but not powering any capability yet.",
    };
  }
  if (descriptor?.connectorKey === "graphrag" && targets.length === 0) {
    return {
      setupCallToAction: "Add source",
      setupDescription: "GraphRAG is bound, but no workspace sources are configured yet.",
    };
  }
  if (targets.length === 0) {
    return {
      setupCallToAction: "Add target",
      setupDescription: "This connector has bindings, but no named targets are configured yet.",
    };
  }
  return {
    setupCallToAction: "Review tools",
    setupDescription:
      descriptor?.connectorKey === "graphrag"
        ? `${targets.length} source${targets.length === 1 ? "" : "s"} configured and ready for agent exposure.`
        : `${bindings.length} connector binding${bindings.length === 1 ? "" : "s"} configured.`,
  };
}

function buildConnectorToolPreviews(params: {
  install: ConnectorInstallRecord;
  descriptor?: ProviderDescriptorRecord;
  bindings: TenantCapabilityBindingRecord[];
  targetsByBindingId: Map<string, TenantCapabilityTargetRecord[]>;
  targetDraft?: { id: string; bindingId: string; fieldValues: Record<string, string> };
}): ConnectorToolPreview[] {
  if (params.install.connectorKey === "graphrag") {
    const previews = params.bindings.flatMap((binding) =>
      (params.targetsByBindingId.get(binding.id) ?? []).map((target) =>
        buildGraphRagToolPreview({
          binding,
          targetKey: target.targetKey,
          displayName: target.displayName,
          workspaceId: target.providerTargetRef,
          metadata: target.metadata,
          status: "saved",
        }),
      ),
    ).filter((entry): entry is ConnectorToolPreview => entry !== null);

    if (params.targetDraft) {
      const draftPreview = buildGraphRagToolPreview({
        binding: params.bindings.find((binding) => binding.id === params.targetDraft?.bindingId),
        targetKey: params.targetDraft.fieldValues.targetKey?.trim() ?? "",
        displayName: params.targetDraft.fieldValues.displayName?.trim() ?? "",
        workspaceId: params.targetDraft.fieldValues.providerTargetRef?.trim() ?? "",
        metadata: buildDraftMetadata(params.targetDraft.fieldValues),
        status: "draft",
      });
      if (draftPreview) {
        const existingIndex = previews.findIndex((preview) => preview.id === draftPreview.id);
        if (existingIndex >= 0) {
          previews.splice(existingIndex, 1, draftPreview);
        } else {
          previews.push(draftPreview);
        }
      }
    }
    return previews.sort((left, right) => left.title.localeCompare(right.title));
  }

  const capabilityDescriptions = new Map(
    (params.descriptor?.capabilities ?? []).map((capability) => [
      capability.capabilityKey,
      capability.description?.trim() || "",
    ] as const),
  );
  return params.bindings
    .map((binding) => ({
      id: binding.id,
      title: binding.capabilityKey,
      description:
        capabilityDescriptions.get(binding.capabilityKey) ||
        `${params.descriptor?.displayName ?? params.install.connectorKey} capability binding.`,
      supportingText: binding.defaultTargetKey ? `Default target ${binding.defaultTargetKey}` : "No default target configured",
      isDefault: false,
      status: "saved" as const,
    }))
    .sort((left, right) => left.title.localeCompare(right.title));
}

function buildGraphRagToolPreview(params: {
  binding: TenantCapabilityBindingRecord | undefined;
  targetKey: string;
  displayName: string;
  workspaceId: string;
  metadata?: JsonObject;
  status: "saved" | "draft";
}): ConnectorToolPreview | null {
  if (!params.targetKey || !params.displayName || !params.workspaceId) {
    return null;
  }
  const toolAlias = readMetadataString(params.metadata, "toolAlias");
  const toolDescription = readMetadataString(params.metadata, "toolDescription");
  return {
    id: `${params.binding?.id ?? "draft"}:${params.targetKey}`,
    title: toolAlias || params.displayName,
    description: toolDescription || `Evidence retrieval scoped to workspace ${params.workspaceId}.`,
    supportingText: `${params.displayName} · ${params.targetKey} · ${params.workspaceId}`,
    isDefault: params.binding?.defaultTargetKey === params.targetKey,
    status: params.status,
  };
}

function buildDraftMetadata(fieldValues: Record<string, string>): JsonObject {
  const metadata: JsonObject = {};
  const toolAlias = fieldValues.toolAlias?.trim();
  const toolDescription = fieldValues.toolDescription?.trim();
  if (toolAlias) {
    metadata.toolAlias = toolAlias;
  }
  if (toolDescription) {
    metadata.toolDescription = toolDescription;
  }
  const rawMetadata = fieldValues.metadata?.trim();
  if (rawMetadata) {
    try {
      const parsed = JSON.parse(rawMetadata);
      if (isJsonObject(parsed)) {
        return { ...parsed, ...metadata };
      }
    } catch {
      return metadata;
    }
  }
  return metadata;
}

function readMetadataString(metadata: JsonObject | undefined, key: string): string {
  const value = metadata?.[key];
  return typeof value === "string" ? value.trim() : "";
}
